/* eslint no-console: 0 */
/*
 * Browser tracing entry point (ERD-7 pilot) — deliberately light.
 *
 * Adapted from mhub-book-web's src/lib/telemetry.js (OTEL, PR #3745) for CRA/CRACO: the build-time
 * variables are REACT_APP_* (CRA inlines them), and the SDK half lives in ./telemetrySdk.js, which
 * initTelemetry() import()s only when the build is configured for tracing. craco.config.js defines
 * every REACT_APP_OTEL_* variable as an explicit empty string when it is unset, so Webpack folds
 * the gate below to `false` and drops both the branch and the lazily imported chunk: a build
 * without an endpoint contains no zone.js and no wrapped fetch or XHR. A build WITH one pays for
 * the SDK in a separate async chunk rather than in the main bundle.
 *
 * Unlike book-web, no caller identity (user.id, mhub.user_id, mhub.company_id) is recorded: the CRM
 * tickets (ERD-3/4/7) forbid tenant and caller enrichment.
 */
import config from '../config';

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// The end of an origin: a path, query, fragment, or the end of the string — nothing else, so
// `https://x.amazonaws.com` cannot match `https://x.amazonaws.com.evil.example`.
const ORIGIN_BOUNDARY = '(?:[/?#]|$)';

const originOf = (url) => {
  try {
    return new URL(url).origin;
  } catch (e) {
    return null;
  }
};

export const toOriginRegExp = (url) => {
  const origin = originOf(url);
  return origin ? new RegExp(`^${escapeRegExp(origin)}${ORIGIN_BOUNDARY}`) : null;
};

const tracedOrigins = [];

// Live lists handed to instrumentation-fetch/-xml-http-request, which re-read them per request.
// Only registered (owned, carrier-ready) origins get traceparent and a CLIENT span; everything else
// — Cognito, S3, Bugsnag, the other AppSync APIs until their backends read the carrier —
// is ignored.
const propagateTraceHeaderCorsUrls = [];
const ignoreUrls = [/^/];

export const registerTracePropagationOrigin = (url) => {
  if (!url) return false;
  const origin = originOf(url);
  if (!origin || tracedOrigins.indexOf(origin) !== -1) return false;

  tracedOrigins.push(origin);
  propagateTraceHeaderCorsUrls.push(toOriginRegExp(origin));
  ignoreUrls[0] = new RegExp(
    `^(?!(?:${tracedOrigins.map(escapeRegExp).join('|')})${ORIGIN_BOUNDARY})`,
  );
  return true;
};

export const isTracedUrl = (url) => tracedOrigins.indexOf(originOf(url)) !== -1;

// Exported for tests only.
export const getTracePropagationOrigins = () => propagateTraceHeaderCorsUrls;
export const getIgnoredUrls = () => ignoreUrls;
export const resetTracePropagationOrigins = () => {
  tracedOrigins.length = 0;
  propagateTraceHeaderCorsUrls.length = 0;
  ignoreUrls[0] = /^/;
};

/**
 * The GraphQL operation name of a request body, or null. Only the name is read — never the query
 * text or the variables, which carry caller data (search terms, ids).
 */
export const graphqlOperationName = (body) => {
  if (typeof body !== 'string' || body.length === 0) return null;
  try {
    const parsed = JSON.parse(body);
    const name = parsed && parsed.operationName;
    return typeof name === 'string' && /^[A-Za-z_][A-Za-z0-9_]{0,100}$/.test(name) ? name : null;
  } catch (e) {
    return null;
  }
};

export const DEFAULT_SAMPLER_RATIO = 1;

export const parseSamplerRatio = (raw) => {
  if (raw === undefined || raw === null || String(raw).trim() === '') return DEFAULT_SAMPLER_RATIO;
  const ratio = Number(raw);
  if (!Number.isFinite(ratio) || ratio < 0 || ratio > 1) {
    console.warn(`[telemetry] REACT_APP_OTEL_TRACES_SAMPLER_ARG "${raw}" is not a number from 0 to 1; `
      + 'sampling every trace');
    return DEFAULT_SAMPLER_RATIO;
  }
  return ratio;
};

/**
 * Whether the OTLP header string carries an `authorization` entry.
 *
 * The collector requires a bearer token on every browser export. Without a header the OTel
 * exporter's browser delegate picks sendBeacon, which cannot attach one, so every export is
 * rejected and the failed requests still cost the page. Tracing therefore stays off unless the
 * build supplied an approved browser-scoped credential (ERD-7). The key is all this reads — the
 * value is never parsed, logged or returned here.
 */
export const hasAuthorizationHeader = (raw) => {
  if (typeof raw !== 'string' || raw.trim() === '') return false;
  return raw.split(',').some((pair) => {
    const index = pair.indexOf('=');
    if (index <= 0) return false;
    return pair.slice(0, index).trim().toLowerCase() === 'authorization'
      && pair.slice(index + 1).trim() !== '';
  });
};

/**
 * Whether this build is configured to trace: an OTLP endpoint AND the header variable that must
 * carry the collector's bearer token. Exported for tests and for callers that want to ask; the
 * gate inside initTelemetry() repeats the condition literally on purpose — see there.
 */
export const isTelemetryConfigured = () => Boolean(
  process.env.REACT_APP_OTEL_EXPORTER_OTLP_ENDPOINT
  && process.env.REACT_APP_OTEL_EXPORTER_OTLP_HEADERS,
);

/**
 * Initialises browser tracing. Resolves to whether it started. Never throws or rejects (fail open).
 *
 * Call AFTER src/index.jsx replaces global.fetch and BEFORE any Apollo/Amplify request. Apollo's
 * createHttpLink resolves the global fetch per request rather than at link construction, so the
 * instrumentation installed when this promise resolves applies to every later request; anything
 * dispatched in the gap simply goes untraced, which is the fail-open behaviour ERD-7 asks for.
 */
export const initTelemetry = async () => {
  // The build-time gate, deliberately written as one inline condition wrapping the whole body
  // rather than `if (!isTelemetryConfigured()) return false`. Webpack folds the inlined
  // `process.env.REACT_APP_*` constants and then drops this entire branch, import() included, so
  // a build without tracing emits no SDK chunk. Behind a helper call or an early return the
  // condition is no longer statically false at the import site and the chunk is emitted anyway.
  if (process.env.REACT_APP_OTEL_EXPORTER_OTLP_ENDPOINT
    && process.env.REACT_APP_OTEL_EXPORTER_OTLP_HEADERS) {
    try {
      if (!hasAuthorizationHeader(process.env.REACT_APP_OTEL_EXPORTER_OTLP_HEADERS)) {
        console.warn('[telemetry] tracing disabled: the OTLP headers variable carries no '
          + 'authorization entry, and the collector rejects unauthenticated browser exports');
        return false;
      }

      if (typeof TextEncoder === 'undefined' || typeof TextDecoder === 'undefined') {
        console.warn('[telemetry] tracing disabled: TextEncoder/TextDecoder unavailable');
        return false;
      }

      // Pilot allowlist: the lead AppSync API only, the one backend that reads the carrier
      // (ERD-5). Registered synchronously so the lists handed to the instrumentation below are
      // already populated, whenever the chunk lands.
      registerTracePropagationOrigin(config.appsync.leadFieldsGraphqlEndpoint);

      const { startTracing } = await import(/* webpackChunkName: "telemetry-sdk" */ './telemetrySdk');
      startTracing({
        endpoint: process.env.REACT_APP_OTEL_EXPORTER_OTLP_ENDPOINT,
        headers: process.env.REACT_APP_OTEL_EXPORTER_OTLP_HEADERS,
        environment: process.env.REACT_APP_RESOURCE_ENVIRONMENT || config.app.stage,
        version: config.app.version,
        samplerRatio: parseSamplerRatio(process.env.REACT_APP_OTEL_TRACES_SAMPLER_ARG),
        propagateTraceHeaderCorsUrls,
        ignoreUrls,
        isTracedUrl,
      });
      return true;
    } catch (e) {
      console.warn('[telemetry] tracing disabled: initialisation failed', e);
      return false;
    }
  }
  return false;
};

export default initTelemetry;
